import {
  AuditDigestSet,
  AuditRecord,
  newPendingRecord,
  newRefusalRecord,
  withResponse,
  withVerdict,
} from '../audit/auditRecord';
import { AuditStore } from '../audit/auditStore';
import { readEpisode } from '../read/readEpisode';
import { NO_RECORD_IN_THIS_BROWSER, SETTLE_FIRST } from '../viewModel/refusals';
import { basilWriteRefusal, BasilWriteSubject } from './canBasilWrite';
import { chunkPlans, PlannedChunk } from './chunk';
import { sendEditContextChunk } from './editContextTransport';
import { preflight } from './preflight';
import { reconcileEditContextReply, Reconciliation } from './reconcile';
import { editContextPath } from './sendGuard';
import { checkStaleness } from './staleness';
import {
  ApplyRefusal,
  ApplyState,
  BasilCanonSet,
  BasilFixture,
  BasilHttp,
  BasilPlan,
  BasilVerifyClient,
  BranchesRead,
  RequestDelivery,
  RequestStatus,
  VerifiablePlan,
  Verdict,
  VerifierArtifactIdentity,
} from './writeTypes';

/**
 * The apply pipeline (spec section 3.5 and sections 5.1-5.8), as one serial function.
 *
 * ```
 * canBasilWrite            → false ⇒ refuse                                  (5.1)
 * verifyClient.handshake() → not available ⇒ refuse 'verifier-unavailable'   (E17)
 * audit store available    → no ⇒ refuse 'audit-unavailable', nothing sent   (E18)
 * fresh full read          → beforeSnapshot (immutable, serialized)          (5.2)
 * verifyClient.canon(fresh)→ the verifier's own per-node lines and digests   (5.3)
 * staleness guard          → ANY drift ⇒ abort                               (E16)
 * per chunk:
 *   re-read the subject    → group, namespace or ACCOUNT moved ⇒ abort, audit (5.1)
 *                          → cannot be re-read ⇒ 'subject-unavailable', nothing sent
 *   re-prove the verifier  → gone or a different artifact ⇒ 'verifier-lost'   (W1.3, every chunk)
 *   require a before-canon → absent ⇒ 'canon-unavailable', nothing sent       (5.3)
 *   audit 'pending'        → transaction awaited BEFORE the request           (5.8)
 *   send one chunk         → one in flight, no blind retry                    (5.4)
 *   reconcile              → mismatch, warnings, 422 or 503 ⇒ abort, audit    (E10, E11, E19)
 *   full re-read + verdict → ALWAYS, the unknown outcome included             (5.7)
 *   audit closed           → verdict recorded ('unknown' when the outcome is unknown, and when
 *                            the after-canon never arrived: an unverified chunk is not a success)
 * ```
 *
 * The three per-chunk proofs are BAR-SHIP-12 and BAR-SHIP-13. A verifier that answered once was
 * treated as answering forever: after a first verdict the controller recorded null after-digests,
 * called that after-read successful, asked for the next before-canon without requiring it, and sent
 * the next chunk into an unproven episode. Nothing is sent now that cannot be proven first, and a
 * chunk that went out and could not be verified afterwards ends the run in a named refusal with its
 * entry left UNRESOLVED — never `done`.
 *
 * Concurrency is ONE, strictly serial, because the engine has no concurrency control and last write
 * wins (AD1:75-80, ADR-0008:67-68). A second apply while one is in flight is refused here, and the
 * chunk loop awaits each request before building the next body.
 */

let applyInFlight = false;

/** True while an apply is running anywhere in the page. */
export function isApplyInFlight(): boolean {
  return applyInFlight;
}

/**
 * S-3b: the pre-write entries THIS SESSION'S apply is still responsible for.
 *
 * The entry is written and awaited before the body is dispatched, so a `pending` row is one of two
 * completely different things: an abandoned entry from a page that died, which only a person can
 * answer, or an entry this very session is about to close, which nobody may answer yet. The row
 * itself cannot tell them apart, so the session says which is which, by key.
 */
const activeWrites = new Set<string>();

const writeKey = (planId: string, chunkIndex: number): string => `${planId}#${String(chunkIndex)}`;

/** True while this session's apply owns the entry, so an attestation must leave it alone. */
export function isActiveWrite(planId: string, chunkIndex: number): boolean {
  return activeWrites.has(writeKey(planId, chunkIndex));
}

let settlementInFlight = false;

/** True while a writer's attestation is being written to the log. */
export function isSettlementInFlight(): boolean {
  return settlementInFlight;
}

/**
 * S-3b: take the write lane for a settlement, or refuse it because an apply is holding it.
 *
 * Settlement and Apply both write the durable log and both read it to decide what to do, so they
 * are serialised against each other the way two applies already are. This is the settlement's half
 * of it; {@link runApply} refuses on the other half. Concurrency across the pair is ONE.
 *
 * @returns false when an apply is in flight and the caller must not write.
 */
export function beginSettlement(): boolean {
  // T23: a second settlement is refused as well, not only an apply. Two admissions shared one
  // boolean, so the first to finish gave the lane back while the other was still writing, and an
  // apply could start over it. The lane has one holder, whichever kind it is.
  if (applyInFlight || settlementInFlight) {
    return false;
  }
  settlementInFlight = true;

  return true;
}

/** Give the write lane back. Always called, whatever the settlement did. */
export function endSettlement(): void {
  settlementInFlight = false;
}

/** What an apply says when a writer's attestation is being written at that moment. */
export const SETTLEMENT_IN_FLIGHT_MESSAGE = 'Basil is saving your confirmation. '
  + 'Give it a moment, then apply again.';

export interface ApplyDeps {
  http: BasilHttp;
  verifyClient: BasilVerifyClient;
  audit: AuditStore;
  /**
   * The page's own subject, re-read before every chunk.
   *
   * A multi-chunk apply takes seconds and the page underneath it is live: the story can go live,
   * the operator can be moved from one namespace to another, the modal's read-only flag can flip.
   * The snapshot taken at preflight is not evidence about chunk two. It is optional in the type
   * only so that a caller which cannot supply it is refused rather than rejected by the compiler:
   * left out, the run stops before the first body with 'subject-unavailable' and sends nothing.
   * The surface always supplies it (`useApply`).
   */
  readSubject?: () => BasilWriteSubject;
  now?: () => number;
  timestamp?: () => string;
  onState?: (state: ApplyState) => void;
  /** Checked between chunks: the operator asked to stop. A request in flight is never abandoned. */
  shouldAbort?: () => boolean;
}

export interface ApplyArgs {
  episodeId: number;
  storyId: number;
  bookId?: number | null;
  subject: BasilWriteSubject;
  plans: readonly BasilPlan[];
  /** the read the script was decompiled from: the staleness baseline. */
  baseline: BranchesRead | null;
  /**
   * The verifier's canon of that same read, taken at decompile time over the worker boundary.
   *
   * It is what the staleness guard compares against, and it is NOT recomputed here from
   * `baseline`: the verifier's canonicalisation belongs to the separately emitted chunk and to
   * nothing in the main bundle (ruling D2, gate check G8). Absent, the guard refuses.
   */
  baselineCanon: BasilCanonSet | null;
}

function emptyState(): ApplyState {
  return {
    phase: 'idle',
    statuses: {},
    verdicts: {},
    refusal: null,
    warnings: [],
    details: [],
  };
}

function touchedNodeIds(chunks: readonly PlannedChunk[]): Set<string> {
  const ids = new Set<string>();

  chunks.forEach((chunk) => {
    chunk.body.nodes.forEach((node) => ids.add(String(node.id)));
  });

  return ids;
}

/** The three projection digests an audit entry records, read off the verifier's own canon. */
export function digestsOfCanon(canon: BasilCanonSet | null): AuditDigestSet | null {
  if (!canon) {
    return null;
  }

  return { v1: canon.v1.digest, v2: canon.v2.digest, layout: canon.layout.digest };
}

/**
 * One chunk carries exactly one plan (`chunk.ts`), so the verdict is asked for with that plan's own
 * request: `simulateApply(before, plan)` applies ONE request (`verify.ts:135-139`).
 */
function verifiablePlanForChunk(chunk: PlannedChunk, plans: readonly BasilPlan[]): VerifiablePlan | null {
  const own = plans.filter((plan) => chunk.planIds.indexOf(plan.planId) !== -1);

  if (own.length !== 1) {
    return null;
  }

  return {
    request: own[0].request,
    targetBranchId: own[0].targetBranchId,
    countsMode: 'edit-context',
    expectedCounts: chunk.expectedCounts,
    expectedDelta: null,
  };
}

interface RunContext {
  deps: ApplyDeps;
  args: ApplyArgs;
  state: ApplyState;
  /** The artifact that answered the handshake this run started with. Every send re-proves it. */
  artifact: VerifierArtifactIdentity | null;
  /**
   * The authenticated user this run is BOUND to, taken from the subject the run started with.
   *
   * BAR-SHIP-13: the per-chunk subject re-read compared environment, story id and group but never
   * the account, so switching the signed-in user between two chunks sent the second chunk and
   * attributed it — like every other entry — to the account that confirmed the first. Every audit
   * entry names this id, by construction, and a live subject whose user id differs from it is
   * refused as `subject-changed` before the next body is built.
   */
  actorUserId: number | null;
}

function publish(context: RunContext): void {
  if (context.deps.onState) {
    context.deps.onState({ ...context.state });
  }
}

/**
 * Record where a set of requests stands, and what is known about the body itself.
 *
 * `delivery` is an annotation and nothing else (P4): every call site states a fact the pipeline has
 * just observed, and no decision anywhere reads it. The gates, the order, the bodies, the verifier
 * and the audit record are exactly what they were.
 */
function setStatuses(
  context: RunContext,
  planIds: readonly string[],
  status: RequestStatus,
  chunkIndex: number,
  message?: string,
  delivery: RequestDelivery = 'not-sent',
): void {
  planIds.forEach((planId) => {
    context.state.statuses[planId] = message === undefined
      ? { status, chunkIndex, delivery }
      : {
        status, chunkIndex, message, delivery,
      };
  });
  publish(context);
}

function refuse(context: RunContext, refusal: ApplyRefusal, message: string): ApplyState {
  context.state.phase = 'aborted';
  context.state.refusal = refusal;
  context.state.details = context.state.details.concat([{ message }]);
  publish(context);

  return context.state;
}

/** Where a mid-run refusal is filed, so it can never overwrite a chunk that already closed. */
interface RefusalKey {
  planId: string;
  chunkIndex: number;
  chunkCount: number;
}

function refusalPlanId(args: ApplyArgs): string {
  return args.plans.length > 0 ? args.plans[0].planId : `refused-${String(args.storyId)}`;
}

async function recordRefusal(context: RunContext, refusal: ApplyRefusal, message: string, key?: RefusalKey): Promise<void> {
  const { args, deps } = context;

  try {
    const record = newRefusalRecord({
      planId: key ? key.planId : refusalPlanId(args),
      chunkIndex: key ? key.chunkIndex : 0,
      chunkCount: key ? key.chunkCount : 0,
      planIds: args.plans.map((plan) => plan.planId),
      userId: context.actorUserId,
      storyId: args.storyId,
      bookId: args.bookId === undefined ? null : args.bookId,
      group: args.subject.story ? args.subject.story.group : null,
      environment: args.subject.environment,
      writePath: 'edit-context',
      countsMode: 'edit-context',
      expectedCounts: null,
      method: 'PATCH',
      path: editContextPath(args.storyId),
      nodeCount: 0,
      byteLength: 0,
      beforeServerTime: args.baseline ? args.baseline.serverTime || null : null,
      ts: deps.timestamp ? deps.timestamp() : undefined,
    }, refusal, message);

    await deps.audit.put(record);
  } catch (error) {
    // The refusal stands whether or not it could be recorded; the durable log is only mandatory
    // before a REQUEST, and a refusal issues none.
  }
}

/**
 * What a message may say about the BODY, given what the pipeline observed about its delivery.
 *
 * T17-01: a sentence that opens "Your change was sent" is a claim of receipt, and a claim of receipt
 * is only ever true for `sent`. The unknown case opens with no claim at all, because the whole point
 * of `unknown` is that neither claim can be made; the recovery view's own instruction follows it.
 * Nothing here is chosen by how the run ended — only by what the transport saw.
 */
const SENT_CLAUSE: Readonly<Record<RequestDelivery, string>> = Object.freeze({
  'not-sent': 'Nothing was sent, and ',
  sent: 'Your change was sent, but ',
  unknown: '',
});

/** The durable log going down AFTER the body left, said from the delivery facts and nothing else. */
export const RECORD_FAILED_MESSAGE: Readonly<Record<RequestDelivery, string>> = Object.freeze({
  'not-sent': "Basil couldn't record this attempt, and nothing was sent.",
  sent: "Your change was sent, but Basil couldn't record it. Reload the episode before applying again.",
  unknown: "Basil couldn't record what happened to this change.",
});

/** The after-read failing, said the same way. */
const afterReadFailed = (delivery: RequestDelivery): string => `${SENT_CLAUSE[delivery]}Basil couldn't check the episode afterwards. Reload to see where it stands.`;

/** The closing entry failing to land, said the same way. */
const verdictRecordFailed = (delivery: RequestDelivery): string => `${SENT_CLAUSE[delivery]}Basil couldn't record how it turned out. Reload the episode before applying again.`;

/**
 * Where one request stands once the transport has answered, from the reply alone.
 *
 * It is separate from {@link RequestDelivery} on purpose (T17-01): this says what is known about the
 * OUTCOME, and delivery says what is known about the BODY. A reply that proves receipt and settles
 * nothing is `mismatched` and `sent`; a body that never left is `refused` and `not-sent`.
 */
function settledStatus(reconciliation: Reconciliation): RequestStatus {
  if (reconciliation.ok) {
    return 'reconciled';
  }

  if (reconciliation.outcomeKnown === false) {
    return 'mismatched';
  }

  return reconciliation.failure === 'count-mismatch' ? 'mismatched' : 'refused';
}

/** What a lost verifier and an unanswerable canon say to the operator. */
export const VERIFIER_LOST_MESSAGE = "Basil's double-check stopped responding, so it stopped here to be safe. Reload the episode to see what went through.";
export const VERIFIER_CHANGED_MESSAGE = "Basil's double-check changed mid-run, so it stopped here to be safe. Reload the episode to see what went through.";
export const CANON_UNAVAILABLE_MESSAGE = "Basil couldn't confirm the episode after the change, so it stopped here to be safe. Reload to see what went through.";
/** What a run with no way to re-read its own subject says to the operator. */
export const SUBJECT_UNAVAILABLE_MESSAGE = "Basil couldn't check this episode and your account again, so it stopped here. Reload to see what went through.";

/**
 * The stop between two sentences, where the first one is not ours to write.
 * A failure message can be the transport's or the server's own words — `reconcile` passes
 * `result.message` through, and that is an axios error's `message` or the reply's `data.error` —
 * and those end without punctuation. Joined to a second sentence by a space they read as one fused
 * sentence ('Network Error Basil is holding …'), so a full stop is added when the text does not
 * already close with one. Trailing whitespace goes first; an empty text stays empty. Nothing else
 * is touched: no wording changes here.
 */
export function endsSentence(text: string): string {
  const trimmed = text.replace(/\s+$/, '');

  if (trimmed === '' || /[.!?]$/.test(trimmed)) {
    return trimmed;
  }

  return `${trimmed}.`;
}

/** A canon is usable only when all three canons arrived with their lines. Anything else is absent. */
function canonIsComplete(canon: BasilCanonSet | null | undefined): canon is BasilCanonSet {
  if (!canon) {
    return false;
  }

  return (['v1', 'v2', 'layout'] as const).every((key) => Boolean(canon[key])
    && Array.isArray(canon[key].lines)
    && Array.isArray(canon[key].digest));
}

function sameArtifact(left: VerifierArtifactIdentity | null, right: VerifierArtifactIdentity | null): boolean {
  if (!left || !right) {
    return false;
  }

  return left.protocol === right.protocol
    && left.buildId === right.buildId
    && left.projectionVersion === right.projectionVersion;
}

/**
 * Is the verifier that identified itself at the start of this run still the one answering?
 *
 * `handshake()` is idempotent on the client and posts nothing once the run has started, so this
 * costs no exchange: it reports the artifact the client currently holds, or `null` once the worker
 * has been dropped — which is what a timeout does. `available` is checked beside it because a
 * client can hold a stale artifact after its worker is gone, and the identity is compared because a
 * DIFFERENT artifact answering mid-run is not the verifier the audit entries name.
 */
async function verifierLoss(context: RunContext): Promise<{ refusal: ApplyRefusal; message: string } | null> {
  const { deps } = context;
  let identity: VerifierArtifactIdentity | null = null;

  try {
    identity = await deps.verifyClient.handshake();
  } catch (error) {
    identity = null;
  }

  if (!identity || deps.verifyClient.available === false) {
    return { refusal: 'verifier-lost', message: VERIFIER_LOST_MESSAGE };
  }

  if (!sameArtifact(context.artifact, identity)) {
    return { refusal: 'verifier-lost', message: VERIFIER_CHANGED_MESSAGE };
  }

  return null;
}

/**
 * One canon exchange, and the NAME of what went wrong when it did not produce a complete canon.
 *
 * BAR-SHIP-12: the old helper swallowed every failure as `null`, so a canon that timed out and a
 * canon that was never asked for were the same value. The controller then recorded null digests,
 * called the after-read successful and sent the next chunk. Naming the failure is what lets the
 * caller stop, and lets the entry say the digests are ABSENT rather than empty.
 */
interface CanonAttempt {
  canon: BasilCanonSet | null;
  refusal: ApplyRefusal | null;
  message: string | null;
}

async function canonAttempt(context: RunContext, fixture: BasilFixture): Promise<CanonAttempt> {
  const { deps } = context;
  let canon: BasilCanonSet | null = null;

  try {
    canon = (await deps.verifyClient.canon(fixture)) || null;
  } catch (error) {
    canon = null;
  }

  if (canonIsComplete(canon)) {
    return { canon, refusal: null, message: null };
  }

  const lost = await verifierLoss(context);

  return lost
    ? { canon: null, refusal: lost.refusal, message: lost.message }
    : { canon: null, refusal: 'canon-unavailable', message: CANON_UNAVAILABLE_MESSAGE };
}

/**
 * What has moved under the apply since it began, as a named refusal, or `null` to carry on.
 *
 * The gate runs again on the LIVE subject, so a story that went live between two chunks is refused
 * by the gate's own name (`story-not-draft`); a namespace, an episode or the signed-in ACCOUNT that
 * changed underneath is `subject-changed`.
 *
 * The account comparison is BAR-SHIP-13. Re-running the gate is not enough: on the test namespace
 * every authenticated user passes it, so switching accounts between two chunks left the gate happy
 * and sent the second chunk under an identity that had confirmed nothing — while every audit entry
 * went on naming the first account. Permission to write is not permission to write as somebody else.
 *
 * SHIP-R2-06: with no way to re-read the subject this check used to return `null` — the one guard
 * in the write lane that failed OPEN on a missing dependency, and it disabled both of the checks
 * above in silence. It now refuses, before the first body, in the direction every other guard here
 * refuses in.
 */
function subjectRefusal(context: RunContext): { refusal: ApplyRefusal; message: string } | null {
  const { args, deps } = context;

  if (!deps.readSubject) {
    return { refusal: 'subject-unavailable', message: SUBJECT_UNAVAILABLE_MESSAGE };
  }

  const live = deps.readSubject();
  const gate = basilWriteRefusal(live);

  if (gate) {
    return { refusal: gate, message: "Basil can't make more changes to this episode right now, so it stopped here. Reload to see what went through." };
  }

  const liveUserId = live.user ? live.user.id : null;

  if (liveUserId !== context.actorUserId) {
    return {
      refusal: 'subject-changed',
      message: 'Your sign-in changed while Basil was working, so it stopped here. Reload to see what went through.',
    };
  }

  if (live.environment !== args.subject.environment) {
    return {
      refusal: 'subject-changed',
      message: 'Something changed in the editor while Basil was working, so it stopped here. Reload to see what went through.',
    };
  }

  const wasId = args.subject.story ? args.subject.story.id : null;
  const nowId = live.story ? live.story.id : null;
  const wasGroup = args.subject.story ? args.subject.story.group : null;
  const nowGroup = live.story ? live.story.group : null;

  if (wasId !== nowId || wasGroup !== nowGroup) {
    return {
      refusal: 'subject-changed',
      message: 'You moved to a different episode while Basil was working, so it stopped here. Reload to see what went through.',
    };
  }

  return null;
}

interface AfterReadArgs {
  context: RunContext;
  chunk: PlannedChunk;
  sent: AuditRecord;
  reconciliation: Reconciliation;
  beforeFixture: BasilFixture;
  verifiable: VerifiablePlan | null;
  /** What the transport observed about the body itself, classified before any record was written. */
  delivery: RequestDelivery;
}

interface AfterReadOutcome {
  ok: boolean;
  message: string | null;
  /**
   * The named refusal, when this failure has one. The durable log going down after a body has gone
   * out is still `audit-unavailable` (E18's family) and must keep saying so: recovery reads the
   * name, and 'the after-read failed' is a different condition with no name of its own.
   */
  refusal: ApplyRefusal | null;
  verdict: Verdict | null;
  snapshot: BranchesRead | null;
  fixture: BasilFixture | null;
}

/**
 * The full re-read, the verdict and the closing audit entry, for one chunk.
 *
 * It runs on the good path AND on an unknown outcome — a resolved 503, a reply carrying no counts,
 * a transport error thrown after a body went out. Those are precisely the cases where the write may
 * have landed, so they are the cases where an after-read is the only evidence there will ever be;
 * returning early left the operator with an entry and no after-state at all. The entry is then
 * closed with `response.outcomeKnown: false` and the state `unknown` — never `reconciled`, and
 * never `failed`, because `unknown` is what keeps it UNRESOLVED and blocks the next apply.
 */
async function afterReadAndVerdict(input: AfterReadArgs): Promise<AfterReadOutcome> {
  const {
    context, chunk, sent, reconciliation, beforeFixture, verifiable, delivery,
  } = input;
  const { args, deps } = context;
  const outcomeKnown = reconciliation.outcomeKnown !== false;

  context.state.phase = 'rereading';
  publish(context);

  const after = await readEpisode(deps.http, args.episodeId);

  if (!after.ok || !after.snapshot || !after.fixture) {
    return {
      ok: false,
      message: after.message || afterReadFailed(delivery),
      refusal: null,
      verdict: null,
      snapshot: null,
      fixture: null,
    };
  }

  context.state.phase = 'verifying';
  publish(context);

  let verdict: Verdict | null = null;
  let verdictMessage: string | null = null;

  try {
    if (verifiable === null) {
      verdictMessage = 'the chunk carried no single plan to verify';
    } else {
      verdict = await deps.verifyClient.verify(beforeFixture, after.fixture, verifiable, reconciliation.counts);
    }
  } catch (error) {
    verdict = null;
    verdictMessage = error instanceof Error ? error.message : String(error);
  }

  // The body has already gone out, so this exchange cannot stop it. What it decides is whether the
  // chunk's outcome was VERIFIED. Absent, the entry closes `unknown` with the digests marked absent
  // and the run ends in a named refusal — it is never carried forward as a success (BAR-SHIP-12).
  const afterCanon = await canonAttempt(context, after.fixture);
  const unverified = afterCanon.canon === null;
  const verdictRecord = withVerdict(sent, {
    ok: Boolean(verdict && verdict.ok),
    changedNodes: verdict ? verdict.changedNodes : [],
    unexpectedPaths: verdict ? verdict.unexpectedPaths : [],
    countMismatch: verdict && verdict.countMismatch ? verdict.countMismatch : null,
    deltaMismatch: verdict && verdict.deltaMismatch ? verdict.deltaMismatch : null,
    verifierArtifact: context.artifact,
    afterServerTime: after.snapshot.serverTime || null,
    afterDigests: digestsOfCanon(afterCanon.canon),
    afterDigestsAbsent: unverified,
    outcomeKnown,
    unverified,
    message: verdict ? null : (verdictMessage || 'the verifier did not return a verdict'),
  });

  try {
    await deps.audit.put(verdictRecord);
  } catch (error) {
    return {
      ok: false,
      message: verdictRecordFailed(delivery),
      refusal: 'audit-unavailable',
      verdict,
      snapshot: after.snapshot,
      fixture: after.fixture,
    };
  }

  if (verdict) {
    context.state.verdicts[chunk.chunkIndex] = verdict;
  }

  if (unverified) {
    // The unresolved `unknown` entry above is this chunk's record and stays exactly as written.
    return {
      ok: false,
      message: afterCanon.message || CANON_UNAVAILABLE_MESSAGE,
      refusal: afterCanon.refusal || 'canon-unavailable',
      verdict,
      snapshot: after.snapshot,
      fixture: after.fixture,
    };
  }

  return {
    ok: true, message: null, refusal: null, verdict, snapshot: after.snapshot, fixture: after.fixture,
  };
}

/**
 * Run one apply.
 *
 * @returns the final `ApplyState`; `phase: 'done'` only when every chunk reconciled and every
 *  verdict came back `ok`.
 */
export async function runApply(deps: ApplyDeps, args: ApplyArgs): Promise<ApplyState> {
  const context: RunContext = {
    deps,
    args,
    state: emptyState(),
    artifact: null,
    actorUserId: args.subject.user ? args.subject.user.id : null,
  };

  if (applyInFlight) {
    return refuse(context, 'plan-refused', 'Basil is still working on your last changes. Give it a moment.');
  }

  // S-3b: the other half of the settlement lock. An apply reads the unresolved rows and decides
  // whether to send from them; an attestation is rewriting those very rows. Nothing starts here
  // while one is being written, exactly as a second apply does not start while the first is out.
  if (settlementInFlight) {
    return refuse(context, 'plan-refused', SETTLEMENT_IN_FLIGHT_MESSAGE);
  }

  applyInFlight = true;

  try {
    context.state.phase = 'preflight';
    publish(context);

    const gate = basilWriteRefusal(args.subject);

    if (gate) {
      await recordRefusal(context, gate, "Basil can't make changes to this episode right now, so nothing was sent.");

      return refuse(context, gate, "Basil can't make changes to this episode right now, so nothing was sent.");
    }

    let artifact: VerifierArtifactIdentity | null = null;

    try {
      artifact = await deps.verifyClient.handshake();
    } catch (error) {
      artifact = null;
    }

    context.artifact = artifact;

    const verifierAvailable = artifact !== null && deps.verifyClient.available !== false;
    const checks = preflight({ subject: args.subject, plans: args.plans, verifierAvailable });

    context.state.statuses = checks.statuses;

    if (!checks.ok && checks.refusal) {
      await recordRefusal(context, checks.refusal, checks.message || 'Basil paused these changes. Nothing was sent.');

      return refuse(context, checks.refusal, checks.message || 'Basil paused these changes. Nothing was sent.');
    }

    let auditAvailable = false;

    try {
      auditAvailable = await deps.audit.available();
    } catch (error) {
      auditAvailable = false;
    }

    if (!auditAvailable) {
      // T17-03: the store would not open. That is all that is known — not WHY, and in particular
      // not that this is a private window. The sentence says the browser and offers the action
      // that fits every cause of it.
      return refuse(context, 'audit-unavailable', NO_RECORD_IN_THIS_BROWSER);
    }

    let unresolved: AuditRecord[] = [];

    try {
      unresolved = await deps.audit.listUnresolved();
    } catch (error) {
      return refuse(context, 'audit-unavailable', NO_RECORD_IN_THIS_BROWSER);
    }

    /**
     * S-3a: THIS EPISODE'S unresolved entries, and only this episode's.
     *
     * THE CHOICE, WRITTEN DOWN: the guard is scoped PER EPISODE, because the two things that
     * answer it already are. The surface reads the log on open through `useApply.mineUnresolved`,
     * which selects `row.story.storyId === storyId`, and the writer's attestation writes back over
     * that same selection. A guard that rejected EVERY unresolved row therefore refused an apply in
     * episode B over a record belonging to episode A, and then named a control that episode B does
     * not show and could not move: the warning and the attestation were never about A's row. The
     * alternative the brief allowed — keep the cross-episode block and route the writer to the
     * blocking episode by name — would need the refusal, the on-open read and the attestation to
     * grow a second, wider scope each, for a safety property the engine does not have: the engine
     * serialises nothing across episodes, so A's uncertainty is not evidence about B. One scope,
     * used by all three, is the consistent choice.
     *
     * `args.storyId` is the id the write lane RECORDS on every entry (`newPendingRecord` below), and
     * `useApply` builds both from the same expression, so the guard and the recovery view can never
     * read the log by different keys.
     */
    const mine = unresolved.filter((row) => row.story && row.story.storyId === args.storyId);

    if (mine.length > 0) {
      // An unknown outcome is named on its own, because it is the case a human has to settle: the
      // request may have been applied and nobody saw. The export path is named with it, since the
      // exported log is the evidence that settling it rests on.
      const unknowns = mine.filter((row) => row.state === 'unknown').length;
      const detail = unknowns > 0
        ? `${unknowns} of them are still not confirmed, so they may already be in the episode`
        : 'their record was left open';

      // S-3: the tail used to say "reload the episode", and reloading does nothing — the record is
      // durable, so the refusal came back on the next load and every load after it. The action that
      // exists is the writer's own attestation, so the sentence names the control that carries it.
      return refuse(
        context,
        'audit-unavailable',
        `Your last changes didn't finish: ${detail}. ${SETTLE_FIRST}`,
      );
    }

    const cut = chunkPlans(checks.sendable);

    if (!cut.ok) {
      await recordRefusal(context, 'plan-refused', cut.message);

      return refuse(context, 'plan-refused', cut.message);
    }

    const { chunks } = cut;
    const before = await readEpisode(deps.http, args.episodeId);

    if (!before.ok || !before.snapshot || !before.fixture) {
      return refuse(context, 'stale-baseline', before.message || "Basil couldn't reload the episode before starting, so nothing was sent.");
    }

    // The staleness baseline is the verifier's, on both sides: the canon of the decompiled read came
    // from the worker at decompile time, and the canon of this read comes from it now.
    const freshCanon = await canonAttempt(context, before.fixture);
    const staleness = checkStaleness({
      baseline: args.baselineCanon,
      fresh: freshCanon.canon,
      touchedNodeIds: touchedNodeIds(chunks),
    });

    if (!staleness.ok) {
      setStatuses(context, checks.sendable.map((plan) => plan.planId), 'stale', 0, staleness.message || 'the episode changed while you were writing');

      return refuse(context, 'stale-baseline', staleness.message || 'This episode changed while you were writing. Reload it, then apply again.');
    }

    let beforeSnapshot: BranchesRead = before.snapshot;
    let beforeFixture: BasilFixture = before.fixture;
    let beforeCanon: CanonAttempt = freshCanon;

    for (let index = 0; index < chunks.length; index += 1) {
      const chunk = chunks[index];
      const refusalKey = {
        planId: chunk.planIds[0], chunkIndex: chunk.chunkIndex, chunkCount: chunks.length,
      };

      if (deps.shouldAbort && deps.shouldAbort()) {
        context.state.phase = 'aborted';
        context.state.details = context.state.details.concat([{ message: 'Basil stopped here. Anything that already went through is in the episode.' }]);
        publish(context);

        return context.state;
      }

      const moved = subjectRefusal(context);

      if (moved) {
        // eslint-disable-next-line no-await-in-loop -- the refusal is recorded before it is surfaced
        await recordRefusal(context, moved.refusal, moved.message, refusalKey);
        setStatuses(context, chunk.planIds, 'stopped', chunk.chunkIndex, moved.message);

        return refuse(context, moved.refusal, moved.message);
      }

      // W1.3 again, before EVERY body — not once, at the start of the run. A verifier that answered
      // the handshake and has since stopped answering is not evidence about this chunk, and neither
      // is a canon the controller never received: with no proven before-state there is nothing to
      // compare an after-state against, so this chunk would be unverifiable the moment it landed.
      // eslint-disable-next-line no-await-in-loop -- one proof per chunk, strictly serial
      const lost = await verifierLoss(context);

      if (lost) {
        // eslint-disable-next-line no-await-in-loop -- the refusal is recorded before it is surfaced
        await recordRefusal(context, lost.refusal, lost.message, refusalKey);
        setStatuses(context, chunk.planIds, 'stopped', chunk.chunkIndex, lost.message);

        return refuse(context, lost.refusal, lost.message);
      }

      if (!canonIsComplete(beforeCanon.canon)) {
        const refusal = beforeCanon.refusal || 'canon-unavailable';
        const message = beforeCanon.message || CANON_UNAVAILABLE_MESSAGE;

        // eslint-disable-next-line no-await-in-loop -- the refusal is recorded before it is surfaced
        await recordRefusal(context, refusal, message, refusalKey);
        setStatuses(context, chunk.planIds, 'stopped', chunk.chunkIndex, message);

        return refuse(context, refusal, message);
      }

      const pending = newPendingRecord({
        planId: chunk.planIds[0],
        chunkIndex: chunk.chunkIndex,
        chunkCount: chunks.length,
        planIds: chunk.planIds,
        userId: context.actorUserId,
        storyId: args.storyId,
        bookId: args.bookId === undefined ? null : args.bookId,
        group: args.subject.story ? args.subject.story.group : null,
        environment: args.subject.environment,
        writePath: 'edit-context',
        countsMode: 'edit-context',
        expectedCounts: chunk.expectedCounts,
        method: 'PATCH',
        path: editContextPath(args.storyId),
        nodeCount: chunk.nodeCount,
        byteLength: chunk.byteLength,
        beforeServerTime: beforeSnapshot.serverTime || null,
        beforeDigests: digestsOfCanon(beforeCanon.canon),
        touchedNodes: chunk.body.nodes.map((node) => String(node.id)),
        ts: deps.timestamp ? deps.timestamp() : undefined,
      });

      // S-3b: this entry is THIS session's from the moment it is written until the run ends. While
      // the key is here a writer's attestation will not touch it, so nothing can settle a request
      // that is still on its way out.
      activeWrites.add(writeKey(pending.planId, pending.chunkIndex));

      try {
        // eslint-disable-next-line no-await-in-loop -- concurrency is one, on purpose
        await deps.audit.putPending(pending);
      } catch (error) {
        return refuse(context, 'audit-unavailable', "Basil couldn't record this change before sending it, so it wasn't sent.");
      }

      context.state.phase = 'sending';
      // The body is about to be dispatched: from here until the reply lands, nobody knows whether
      // the engine has it. `unknown` is the honest answer for exactly that window.
      setStatuses(context, chunk.planIds, 'sending', chunk.chunkIndex, undefined, 'unknown');

      // eslint-disable-next-line no-await-in-loop -- one request in flight, strictly serial
      const result = await sendEditContextChunk({
        http: deps.http, storyId: args.storyId, body: chunk.body, now: deps.now,
      });
      const reconciliation = reconcileEditContextReply(result, chunk.expectedCounts);
      // T17-01. Delivery is classified from the DISPATCH and RECEIPT facts the transport observed,
      // and it is filed HERE — before the record is written, before the after-read, before any
      // decision about the outcome. A record that fails to land afterwards changes what is known
      // about the OUTCOME and nothing at all about whether the body left.
      const { delivery } = result;
      const outcomeUnknown = reconciliation.outcomeKnown === false;
      const failureMessage = reconciliation.message || "Your change was sent, but the episode doesn't look the way Basil expected.";

      setStatuses(
        context,
        chunk.planIds,
        settledStatus(reconciliation),
        chunk.chunkIndex,
        reconciliation.ok ? undefined : failureMessage,
        delivery,
      );

      const sent = withResponse(pending, {
        status: result.status,
        counts: reconciliation.counts,
        warnings: reconciliation.warnings,
        details: reconciliation.details,
        ms: result.ms,
        outcomeKnown: result.outcomeKnown,
        message: result.message,
      });

      try {
        // eslint-disable-next-line no-await-in-loop -- the record must land before the next step
        await deps.audit.put(sent);
      } catch (error) {
        return refuse(context, 'audit-unavailable', RECORD_FAILED_MESSAGE[delivery]);
      }

      context.state.warnings = context.state.warnings.concat(reconciliation.warnings);
      context.state.details = context.state.details.concat(reconciliation.details);

      // A KNOWN failure — a 422, engine warnings, counts that do not match, a local refusal that
      // never sent — is settled: nothing further will be learned, and the queue stops without a
      // further read. The status and the delivery are already filed above.
      if (!reconciliation.ok && !outcomeUnknown) {
        context.state.phase = 'aborted';
        context.state.details = context.state.details.concat([{ message: failureMessage }]);
        publish(context);

        return context.state;
      }

      const verifiable = verifiablePlanForChunk(chunk, checks.sendable);
      // eslint-disable-next-line no-await-in-loop -- the after-read belongs to this chunk
      const closed = await afterReadAndVerdict({
        context, chunk, sent, reconciliation, beforeFixture, verifiable, delivery,
      });

      if (outcomeUnknown) {
        // The outcome could not be observed. The request may be in the episode and may not be, and
        // nothing claims either — but whether the BODY left is a separate fact, and it keeps the
        // answer the transport gave it: a reply with no counts proves receipt, a lost reply does not.
        setStatuses(context, chunk.planIds, 'mismatched', chunk.chunkIndex, failureMessage, delivery);

        const said = closed.ok
          ? `${endsSentence(failureMessage)} Basil is holding new changes until this is settled.`
          : `${endsSentence(failureMessage)} ${String(closed.message)}`;

        // An unobserved outcome carries no refusal of its own (the plan status and the detail say
        // it), but a NAMED failure on top of it keeps its name: recovery reads the name.
        if (closed.refusal) {
          return refuse(context, closed.refusal, said);
        }

        context.state.phase = 'aborted';
        context.state.details = context.state.details.concat([{ message: said }]);
        publish(context);

        return context.state;
      }

      if (!closed.ok) {
        if (closed.refusal) {
          return refuse(context, closed.refusal, closed.message || afterReadFailed(delivery));
        }
        context.state.phase = 'aborted';
        context.state.details = context.state.details.concat([{ message: closed.message || afterReadFailed(delivery) }]);
        publish(context);

        return context.state;
      }

      if (!closed.verdict || !closed.verdict.ok) {
        // No rollback: there is no owner's word for a compensating write (LANE:70-71).
        setStatuses(context, chunk.planIds, 'rejected', chunk.chunkIndex, "Your change was sent, but Basil's double-check couldn't confirm this change.", delivery);
        context.state.phase = 'aborted';
        publish(context);

        return context.state;
      }

      setStatuses(context, chunk.planIds, 'verified', chunk.chunkIndex, undefined, delivery);
      beforeSnapshot = closed.snapshot as BranchesRead;
      beforeFixture = closed.fixture as BasilFixture;

      if (index + 1 < chunks.length) {
        // The next chunk's before-state is this chunk's after-read, so its digests are asked for
        // once — and only when there is a next chunk to record them against. The ATTEMPT is kept,
        // not just its value: the next pass round the loop requires a complete canon before it
        // builds a body, and it names the failure it found here.
        // eslint-disable-next-line no-await-in-loop -- one exchange at a time, on purpose
        beforeCanon = await canonAttempt(context, beforeFixture);
      }
    }

    context.state.phase = 'done';
    publish(context);

    return context.state;
  } finally {
    applyInFlight = false;
    // The run is over, however it ended. Anything it left `pending` is now ABANDONED rather than in
    // flight, which is exactly the entry a writer's attestation is allowed to answer (S-3b).
    activeWrites.clear();
  }
}
